import { getString, setString, removeValue } from "@/utils/SecureLocalStorage";
import { getAdministratorClientId, getClientId } from "@/data/repository/ClientRepository";
import { storage } from "@/main";

import JSZip from "jszip";
import Report from "../models/Report";

const KEY_SINGLE_TIME_ENTRY_REPORT = "STE";

export function hashRef(ref: string) {
  let hash = 0;

  if (ref.length == 0) {
    return hash.toString();
  }

  for (let i = 0; i < ref.length; i++) {
    const char = ref.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  return hash.toString();
}

export async function uploadImage(imageName: string, file: File) {
  const completeRef = `/images/${getClientId()}/${imageName}`;
  return storage.ref(completeRef).put(file);
}

const CACHE_TTL = 12 * 60 * 60 * 1000; // 12 hours in ms

export async function getDownloadUrl(ref: string): Promise<string> {
  const hash = hashRef(ref);
  const urlKey = hash;
  const tsKey  = hash + "_ts";

  const cachedUrl = getString(urlKey);
  const savedTs   = parseInt(getString(tsKey) || "0", 10);

  // If we have a URL and it’s younger than our TTL, return it
  if (cachedUrl && Date.now() - savedTs < CACHE_TTL) {
    return cachedUrl;
  }

  // Otherwise (first time or expired), fetch a new one…
  const freshUrl = await storage.ref(ref).getDownloadURL();
  setString(urlKey, freshUrl);
  setString(tsKey, Date.now().toString());
  return freshUrl;
}
/** Invalidates the cached download URL for a storage ref so the next call fetches fresh. */
export function invalidateDownloadUrlCache(ref: string): void {
  const hash = hashRef(ref);
  removeValue(hash);
  removeValue(hash + "_ts");
}
export async function getImageUrl(ref: string): Promise<string> {
  const completeRef = `/images/${getClientId()}/${ref}`;

  return getDownloadUrl(completeRef);
}

export async function getSharedImageUrl(ref: string): Promise<string> {
  const completeRef = `/images/${getAdministratorClientId()}/${ref}`;

  return getDownloadUrl(completeRef);
}

export async function getReportDownloadUrl(ref: string) {
  let adjustedRef = ref

  if (!adjustedRef.startsWith("/reports")) {
    adjustedRef = `/reports/${getClientId()}/${ref}`
  }

  const hash = hashRef(adjustedRef);
  const cashedUrl = getString(hash);

  if (cashedUrl) {
    return cashedUrl;
  } else {
    const result = await storage.ref(adjustedRef).getDownloadURL();
    setString(hash, result);
    return result;
  }
}

export async function getImageDownloadUrl(ref: string) {
  let adjustedRef = ref

  if (!adjustedRef.startsWith("/images")) {
    adjustedRef = `/images/${getClientId()}/${ref}`
  }

  const hash = hashRef(adjustedRef);
  const cashedUrl = getString(hash);

  if (cashedUrl) {
    return cashedUrl;
  } else {
    const result = await storage.ref(adjustedRef).getDownloadURL();
    setString(hash, result);
    return result;
  }
}

export async function getSingleTimeEntryDownloadUrl(ref: string) {
  let adjustedRef = ref

  if (!adjustedRef.startsWith("/singleTimeEntries")) {
    adjustedRef = `/singleTimeEntries/${getClientId()}/${ref}`
  }

  const hash = hashRef(adjustedRef);
  const cashedUrl = getString(hash);

  if (cashedUrl) {
    return cashedUrl;
  } else {
    const result = await storage.ref(adjustedRef).getDownloadURL();
    setString(hash, result);
    return result;
  }
}

/**
 * Fetches all provided document references, determines whether each ref is for a report (contains '/')
 * or an image (no '/'), retrieves the corresponding download URL using getReportDownoladUrl or
 * getImageDownoladUrl, downloads and zips each file blob, and returns a blob URL for the resulting
 * zip archive.
 * @param refs - Array of storage references. Refs containing '/' are treated as report paths; refs
 *               without '/' are treated as image names.
 * @returns A Promise<string> that resolves to a blob URL pointing to the generated zip file
 *          containing all requested documents.
 */
export async function getAllDocumentsDownloadUrl(
  refs: string[],
  reports: Array<Report>,
): Promise<string> {
  const zip = new JSZip();
  for (const ref of refs) {
    try {
      let url: string;
      if (ref.includes('/')) {
        url = await getReportDownloadUrl(ref);
      } else {
        if (ref.includes(KEY_SINGLE_TIME_ENTRY_REPORT)) {
          url = await getSingleTimeEntryDownloadUrl(ref);
        }
        else {
          url = await getImageDownloadUrl(ref);
        }
      }
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.statusText}`);
      }

      const blob = await response.blob();
      
      const report = reports.find((r) => r.storageLocation.includes(ref));

      const fileNameFallback = ref.split("/").pop() || ref;
      const filename = (report?.fileNameToDownload ?? fileNameFallback).replaceAll(":", ".");
      zip.file(filename, blob);
    } catch (error) {
      continue;
    }
  }
  const content = await zip.generateAsync({ type: "blob" });
  return URL.createObjectURL(content);
}
